package dev.kuml.portal.web.share

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.api.ShareSvgResult
import dev.kuml.portal.web.api.revokeBlobUrlIfAny
import dev.kuml.portal.web.components.languageSwitcher
import dev.kuml.portal.web.components.legalLinks
import dev.kuml.portal.web.editor.DiagramFileName
import dev.kuml.portal.web.editor.triggerBlobUrlDownload
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.InputType
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import kotlinx.browser.window
import kotlinx.coroutines.launch

/**
 * R-21's PUBLIC, UNAUTHENTIFIZIERTE Betrachterseite (`/s/:token`) -- der einzige Bildschirm dieser
 * App, der ganz ohne Sitzung funktioniert. Ruft AUSSCHLIESSLICH die drei `/api/share/…`-Routen auf;
 * insbesondere NIE `api.me()`, `api.usage()` oder irgendeine Projekt-/Diagramm-Route, deren 401 den
 * Session-Interceptor ausloesen und einen anonymen Betrachter nach `/login` werfen wuerde (siehe
 * `PortalApi.publicShare`/`unlockShare`/`publicShareSvg`, die bewusst `publicJsonRequest`
 * verwenden, nicht `authenticatedJsonRequest`).
 *
 * Zustaende: Lade -> (Passwortabfrage) -> Diagramm | „Link ungueltig" | „Noch kein Diagramm" |
 * „Kann derzeit nicht dargestellt werden".
 *
 * Kein Projektname, kein Teamname, kein Erstellername irgendwo im DOM -- diese Daten liefert der
 * Server hier strukturell nicht (siehe `PublicShareResponse`'s KDoc serverseitig).
 */
public fun Container.publicShareScreen(
    api: PortalApi,
    token: String,
    onImpressumClick: () -> Unit,
    onDatenschutzClick: () -> Unit,
    onAgbClick: () -> Unit,
) {
    val card = div(className = "kportal-public-share")
    val statusDiv = card.div(className = "kportal-status-area") { setAttribute("aria-live", "polite") }
    val bodyHost = card.div()

    var currentSvgBlobUrl: String? = null

    fun setStatus(message: String) {
        statusDiv.content = message
    }

    fun showSvg(svgBlobUrl: String) {
        bodyHost.removeAll()
        val previous = currentSvgBlobUrl
        val stage = bodyHost.div(className = "kportal-public-share-stage")
        stage.image(src = svgBlobUrl, alt = "")
        currentSvgBlobUrl = svgBlobUrl
        revokeBlobUrlIfAny(previous)

        val actions = bodyHost.div(className = "kportal-public-share-actions")
        val downloadButton = actions.button(t.share.publicDownloadSvg, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
        downloadButton.onClick {
            triggerBlobUrlDownload(
                svgBlobUrl,
                DiagramFileName.forDiagramName(t.editor.diagramFileFallback, "svg", t.editor.diagramFileFallback),
            )
        }
    }

    fun showMessage(
        title: String,
        detail: String,
    ) {
        bodyHost.removeAll()
        bodyHost.div(className = "kportal-public-share-message") {
            div(title, className = "kportal-label")
            div(detail, className = "kportal-attach-hint")
        }
    }

    fun loadSvg(viewToken: String?) {
        setStatus(t.share.publicLoading)
        AppScope.launch {
            when (val result = api.publicShareSvg(token, viewToken)) {
                is ShareSvgResult.Rendered -> {
                    setStatus("")
                    showSvg(result.svgBlobUrl)
                }
                ShareSvgResult.Empty -> {
                    setStatus("")
                    showMessage(t.share.publicEmptyTitle, t.share.publicEmptyDetail)
                }
                ShareSvgResult.NotFound -> {
                    setStatus("")
                    showMessage(t.share.publicNotFoundTitle, t.share.publicNotFoundDetail)
                }
                is ShareSvgResult.ValidationFailed -> {
                    setStatus("")
                    showMessage(t.share.publicRenderFailed, result.message ?: "")
                }
                ShareSvgResult.PasswordRequired -> {
                    // Handled by the caller (unlock flow) -- reaching this here would only mean
                    // the view token expired between the successful unlock and this call.
                    setStatus("")
                    showMessage(t.share.publicPasswordPromptTitle, "")
                }
                is ShareSvgResult.HttpError -> {
                    setStatus("")
                    showMessage(t.share.publicRenderFailed, "")
                }
                ShareSvgResult.NetworkError -> {
                    setStatus("")
                    showMessage(t.share.publicRenderFailed, "")
                }
            }
        }
    }

    fun showPasswordPrompt() {
        bodyHost.removeAll()
        val form = bodyHost.div(className = "kportal-public-share-locked")
        form.div(t.share.publicPasswordPromptTitle, className = "kportal-label")
        val passwordInput: TextInput = form.textInput(type = InputType.PASSWORD, className = "kportal-input")
        passwordInput.placeholder = t.share.publicPasswordPlaceholder
        val errorSpan = form.div("", className = "kportal-status-error")
        val unlockButton = form.button(t.share.publicUnlockButton, className = "kportal-btn kportal-btn-primary kportal-btn-auto")

        fun attemptUnlock() {
            val password = passwordInput.value.orEmpty()
            if (password.isEmpty()) return
            errorSpan.content = ""
            unlockButton.disabled = true
            unlockButton.text = t.share.publicUnlocking
            AppScope.launch {
                when (val result = api.unlockShare(token, password)) {
                    is ApiResult.Success -> loadSvg(result.value.viewToken)
                    is ApiResult.HttpError -> {
                        unlockButton.disabled = false
                        unlockButton.text = t.share.publicUnlockButton
                        errorSpan.content =
                            if (result.status == 401) {
                                t.share.publicWrongPassword
                            } else {
                                t.errors.forCode(result.error?.code, result.status) ?: t.share.publicWrongPassword
                            }
                    }
                    ApiResult.NetworkError -> {
                        unlockButton.disabled = false
                        unlockButton.text = t.share.publicUnlockButton
                        errorSpan.content = t.errors.networkError
                    }
                }
            }
        }
        unlockButton.onClick { attemptUnlock() }
        passwordInput.setEventListener<TextInput> {
            keydown = { e ->
                if (e.key == "Enter") {
                    e.preventDefault()
                    attemptUnlock()
                }
            }
        }
    }

    setStatus(t.share.publicLoading)
    AppScope.launch {
        when (val result = api.publicShare(token)) {
            is ApiResult.Success -> {
                setStatus("")
                if (result.value.passwordRequired) {
                    showPasswordPrompt()
                } else {
                    loadSvg(null)
                }
            }
            is ApiResult.HttpError -> {
                setStatus("")
                showMessage(t.share.publicNotFoundTitle, t.share.publicNotFoundDetail)
            }
            ApiResult.NetworkError -> {
                setStatus("")
                showMessage(t.share.publicRenderFailed, "")
            }
        }
    }

    val footer = div(className = "kportal-public-share-footer")
    footer.div {
        val cta = button(t.share.publicPoweredByLink, className = "kportal-legal-link")
        cta.setAttribute("rel", "noopener noreferrer")
        cta.onClick { window.open("https://kuml.dev", "_blank", "noopener,noreferrer") }
    }
    footer.languageSwitcher()
    footer.legalLinks(onImpressumClick, onDatenschutzClick, onAgbClick)
}
